import React from "react"

import { Marker } from "@react-google-maps/api"

import {
  setDeviceMuteStatusAsync,
  toggleNotificationsFromDevicesAsync,
} from "@/base/farmhq-api"
import * as Models from "@/redux/models"
import {
  getDeviceStatusDataByDeviceIdFromState,
} from "@/redux/device-event-last.reducer"
import { getFarmUserPermissionsFromState } from "@/redux/user-permissions.reducer"
import { useRootDispatch } from "@/redux/useRootDispatch"
import { useRootSelector } from "@/redux/useRootSelector"
import { Theme } from "@/theme"

const OVERLAY_SIZE = 28

// Ionicons "notifications" bell path (scaled to fit inside the circle)
const BELL_PATH =
  "M12 22c1.1 0 2-.9 2-2h-4a2 2 0 0 0 2 2zm6-6v-5c0-3.07-1.63-5.64-4.5-6.32V4c0-.83-.67-1.5-1.5-1.5s-1.5.67-1.5 1.5v.68C7.64 5.36 6 7.92 6 11v5l-2 2v1h16v-1l-2-2z"
// Ionicons "notifications-off" bell-with-slash path
const BELL_OFF_PATH =
  "M12 22c1.1 0 2-.9 2-2h-4a2 2 0 0 0 2 2zm6-6v-5c0-3.07-1.63-5.64-4.5-6.32V4c0-.83-.67-1.5-1.5-1.5s-1.5.67-1.5 1.5v.68C7.64 5.36 6 7.92 6 11v5l-2 2v1h16v-1l-2-2zM3.27 3L2 4.27l2.18 2.18C4.07 7.21 4 8.07 4 9v5l-2 2v1h14.73l1 1L20 15.73 3.27 3z"

function makeIcon(color: string, isSilent: boolean): google.maps.Icon {
  const iconPath = isSilent ? BELL_OFF_PATH : BELL_PATH
  // Circle with bell icon inside, using a 24x24 viewBox for the icon path
  const svg = `<svg xmlns="http://www.w3.org/2000/svg" width="${OVERLAY_SIZE}" height="${OVERLAY_SIZE}" viewBox="0 0 ${OVERLAY_SIZE} ${OVERLAY_SIZE}">
    <circle cx="${OVERLAY_SIZE / 2}" cy="${OVERLAY_SIZE / 2}" r="${OVERLAY_SIZE / 2 - 1}" fill="${color}" stroke="white" stroke-width="2"/>
    <g transform="translate(${(OVERLAY_SIZE - 16) / 2}, ${(OVERLAY_SIZE - 16) / 2}) scale(0.667)">
      <path d="${iconPath}" fill="white"/>
    </g>
  </svg>`
  return {
    scaledSize: new google.maps.Size(OVERLAY_SIZE, OVERLAY_SIZE),
    url: `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(svg)}`,
  }
}

/**
 * Single notification status indicator for a device on the web map.
 * Admin users can tap to cycle through all three states.
 */
function DeviceNotificationIndicatorWeb({
  deviceId,
}: Models.DeviceIdProps): React.JSX.Element | null {
  const dispatch = useRootDispatch()

  const gpsLocation = useRootSelector((state) => {
    return getDeviceStatusDataByDeviceIdFromState(state, deviceId)?.gpsLocation
  })

  const isMuted = useRootSelector(
    (state) =>
      Models.deviceConfiguration.selectById(state, deviceId)
        ?.notificationsMuted === true,
  )

  const isSuppressed = useRootSelector(
    (state) =>
      Models.deviceUserAssociation.selectById(state, deviceId)
        ?.notificationsSuppressed === true,
  )

  const canManage = useRootSelector(
    (state) =>
      getFarmUserPermissionsFromState(state)
        ?.canManageDeviceNotifications === true,
  )

  const handleClick = React.useCallback(() => {
    if (canManage) {
      // Admin cycle: active → muted for me → muted for all → active
      if (!isSuppressed && !isMuted) {
        dispatch(
          toggleNotificationsFromDevicesAsync({
            action: "SUPPRESS",
            ids: [deviceId],
          }),
        )
      } else if (isSuppressed && !isMuted) {
        dispatch(
          toggleNotificationsFromDevicesAsync({
            action: "ENABLE",
            ids: [deviceId],
          }),
        )
        dispatch(setDeviceMuteStatusAsync({ deviceId, muted: true }))
      } else if (isMuted) {
        // Also clear suppression when transitioning back to active
        dispatch(setDeviceMuteStatusAsync({ deviceId, muted: false }))
        if (isSuppressed) {
          dispatch(
            toggleNotificationsFromDevicesAsync({
              action: "ENABLE",
              ids: [deviceId],
            }),
          )
        }
      }
    } else {
      if (isMuted) {
        return
      }
      dispatch(
        toggleNotificationsFromDevicesAsync({
          action: isSuppressed ? "ENABLE" : "SUPPRESS",
          ids: [deviceId],
        }),
      )
    }
  }, [canManage, deviceId, dispatch, isMuted, isSuppressed])

  if (!gpsLocation?.web) {
    return null
  }

  let dotColor: string
  if (isMuted) {
    dotColor = Theme.COLORS.$gray[400]
  } else if (isSuppressed) {
    dotColor = Theme.COLORS.$error[500]
  } else {
    dotColor = Theme.COLORS.$success[500]
  }

  const isSilent = isMuted || isSuppressed
  const icon = React.useMemo(() => makeIcon(dotColor, isSilent), [dotColor, isSilent])

  return (
    <Marker
      icon={icon}
      position={gpsLocation.web}
      zIndex={1000}
      onClick={handleClick}
    />
  )
}

/**
 * Renders notification status indicators on the web map for all visible devices.
 */
export function DeviceNotificationOverlayWeb({
  deviceIds,
}: {
  deviceIds: string[]
}): React.JSX.Element {
  return (
    <React.Fragment>
      {deviceIds.map((deviceId) => (
        <DeviceNotificationIndicatorWeb key={deviceId} deviceId={deviceId} />
      ))}
    </React.Fragment>
  )
}
